<!-- eslint-disable vue/no-v-html -->
<template>
  <card
    v-if="content || customComponent"
    :class="[
      'info-card',
      { 'format-html': formatHtml, 'hide-close': hasBackLink },
    ]"
    :card-id="id + '-info-card'"
    :title="title"
    :show-back-link="hasBackLink"
    :back-link-label="backLinkLabel"
    @go-back="handleClose"
    @close-card="handleClose"
  >
    <div
      v-if="!isContentList && content"
      class="content"
      v-html="content.trim()"
    />
    <div v-else-if="isContentList && content" class="content">
      <ul class="info-list">
        <li v-for="(info, index) in content" :key="index">
          <x-heading size="5">
            {{ info.name }}
          </x-heading>
          <x-body-text size="1">
            {{ info.description.trim() }}
          </x-body-text>
        </li>
      </ul>
    </div>
    <div v-if="customComponent" class="content">
      <component :is="customComponent" v-bind="customProps" />
    </div>
  </card>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import { reactive, shallowRef } from 'vue'

import card from '@/components/shared/card'
import xHeading from '@/components/shared/x-heading'
import xBodyText from '@/components/shared/x-body-text'

const STATE_DEFAULTS = {
  id: '',
  title: '',
  content: null,
  formatHtml: false,
  backLinkLabel: '',
  customComponent: null,
  customProps: {},
}

const state = reactive({
  id: STATE_DEFAULTS.id,
  title: STATE_DEFAULTS.title,
  content: STATE_DEFAULTS.content,
  formatHtml: STATE_DEFAULTS.formatHtml,
  backLinkLabel: STATE_DEFAULTS.backLinkLabel,
})

/**
 * The customComponent prop is a workaround for not being able to nest slots through multiple cards here
 * You can send a Vue component as the customComponent prop if you need to do more specific formatting
 * Use the shallowRef to not potentially be storing reactive components
 */
const shallowState = shallowRef({
  customComponent: STATE_DEFAULTS.customComponent,
})

// Setters

/* eslint-disable jsdoc/no-types -- Intentionally keeping {Object}: removing it causes TypeScript
   to infer a strict required type from the destructured params, breaking callers that omit
   optional fields. The proper fix is adding explicit TypeScript types in a follow-up. */
/**
 * Set more info to open card
 * @param {Object} newVal
 */
/* eslint-enable jsdoc/no-types */
export function setMoreInfo({
  id,
  title,
  content,
  formatHtml = false,
  backLinkLabel,
  customComponent,
  customProps,
}) {
  state.id = id
  state.title = title
  state.content = content
  state.formatHtml = formatHtml
  state.backLinkLabel = backLinkLabel
  state.customProps = customProps
  shallowState.value = { customComponent }
}

/**
 * Clears more info and closes card
 */
export function clearMoreInfo() {
  state.id = STATE_DEFAULTS.id
  state.title = STATE_DEFAULTS.title
  state.content = STATE_DEFAULTS.content
  state.formatHtml = STATE_DEFAULTS.formatHtml
  state.backLinkLabel = STATE_DEFAULTS.backLinkLabel
  state.customProps = STATE_DEFAULTS.customProps
  shallowState.value = STATE_DEFAULTS.customComponent
}

export default {
  components: {
    card,
    xHeading,
    xBodyText,
  },
  computed: {
    customComponent() {
      return shallowState.value?.customComponent
    },
    customProps() {
      return state.customProps
    },
    id() {
      return state.id
    },
    title() {
      return state.title
    },
    content() {
      return state.content
    },
    formatHtml() {
      return state.formatHtml
    },
    backLinkLabel() {
      return state.backLinkLabel
    },
    hasBackLink() {
      return !!this.backLinkLabel
    },
    isContentList() {
      return Array.isArray(state.content) && state.content.length > 0
    },
  },
  watch: {
    $route(to, from) {
      // Make sure to close card if the user exits via alternative methods
      if (this.content && to !== from) {
        this.handleClose()
      }
    },
  },
  methods: {
    handleClose() {
      clearMoreInfo()
    },
  },
}
</script>

<!-- eslint-disable vue/enforce-style-attribute -->
<style lang="scss">
@use '@/styles/system/_type';
@use '@/styles/system/breakpoints';
@import '@/styles/_variables.scss';

.info-card {
  .content {
    padding: 0 var(--ys-spacing-250) var(--ys-spacing-150) var(--ys-spacing-250);

    height: 95vh;
    white-space: pre-line;

    .info-list {
      h5 {
        margin-bottom: 2px;
      }

      .body1 {
        margin-bottom: 35px;
      }
    }
  }

  &.format-html {
    font-size: 14px;

    h1,
    h2,
    h3,
    h4,
    h5 {
      margin-top: 0.8em;
    }

    h1 {
      @include type.title4;
    }

    h2 {
      @include type.subtitle3;
    }

    ul {
      list-style: disc;
      padding-left: 1rem;
    }
  }

  &.hide-close {
    .close {
      display: none;
    }
  }
}
</style>
